import { Injectable } from '@angular/core';
import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { environment } from './../../environments/environment';
import { EncryptionService } from './../shared/service/common/encryption.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AxiosService {
  private axiosInstance: AxiosInstance | null = null;
  private axiosInitialized: Promise<void>;

  constructor(
    private readonly encryptService: EncryptionService,
    private router: Router,
  ) {
    this.axiosInitialized = this.initAxios();
  }

  private async initAxios(): Promise<void> {
    const token = localStorage.getItem('secTkn');
    const cryptFactor = await this.encryptService.encrypt(`${environment.secrets.appCode}`);

    this.axiosInstance = axios.create({
      baseURL: environment.apiUrl,
      timeout: 500000,
      headers: {
        'Content-Type': 'application/json',
        factor: cryptFactor,
        Authorization: token ? `Bearer ${token}` : '',
      },
    });

    this.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Verificar si existe el token en localStorage
        const currentToken = localStorage.getItem('secTkn');

        // // Si no hay token y la URL no es de login, redirigir al login
        // if (!currentToken && !config.url?.includes('/login')) {
        //   // this.router.navigate(['/login']);
        //   return Promise.reject('No hay token de autenticación');
        // }

        // Si hay token, asegurarse de que esté en los headers
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }

        return config;
      },
      (error: any) => Promise.reject(error),
    );

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (response.status >= 200 && response.status <= 300) {
          return response;
        } else {
          throw new Error('API request failed');
        }
      },
      async (error: any) => {
        // Si el error es por token inválido
        if (error.response?.status === 401) {
          // Limpiar el token
          localStorage.removeItem('secTkn');
          // Redirigir al login
          // this.router.navigate(['/login']);
        }
        return Promise.reject(error);
      },
    );
  }

  private async ensureAxiosInitialized(): Promise<void> {
    // Verificar si existe el token en localStorage
    const token = localStorage.getItem('secTkn');

    // Si no hay instancia de axios o no hay token, reinicializar
    if (!this.axiosInstance || !token) {
      await this.initAxios();
    }
  }

  async get<T>(url: string, config?: InternalAxiosRequestConfig): Promise<T> {
    await this.ensureAxiosInitialized();
    try {
      const response: AxiosResponse<T> = await this.axiosInstance!.get<T>(url, config);
      return response.data;
    } catch (error) {
      //   console.error('ERROR');
      this.handleError(error);
      throw error;
    }
  }

  async post<T>(url: string, data: any, config?: InternalAxiosRequestConfig): Promise<T> {
    await this.ensureAxiosInitialized();
    try {
      const response: AxiosResponse<T> = await this.axiosInstance!.post<T>(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async put<T>(url: string, data: any, config?: InternalAxiosRequestConfig): Promise<T> {
    await this.ensureAxiosInitialized();
    try {
      const response: AxiosResponse<T> = await this.axiosInstance!.put<T>(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async patch<T>(url: string, data: any, config?: InternalAxiosRequestConfig): Promise<T> {
    await this.ensureAxiosInitialized();
    try {
      const response: AxiosResponse<T> = await this.axiosInstance!.patch<T>(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async delete<T>(url: string, config?: InternalAxiosRequestConfig): Promise<T> {
    await this.ensureAxiosInitialized();
    try {
      const response: AxiosResponse<T> = await this.axiosInstance!.delete<T>(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  private handleError(error: any): void {
    console.error('Error occurred:', error);
  }
}
